import React, { useEffect, useState } from "react";
import { Table, Badge, Dropdown } from "react-bootstrap";
import {
  getDiscoverPriorityList,
  updateDiscoverPriority,
} from "../../services/UserServices/UserService";
import Spinner from "../components/Common/Spinner";
import { notifyError, notifyTopRight } from "../components/Common/Toaster";
import Pagination from "../components/Common/Pagination";
import moment from "moment";
import dummmyImg from "../../images/profile/images1.png";
import {
  DISCOVER_PRIORITY_OPTIONS,
  getDiscoverPriorityBadgeClass,
  getDiscoverPriorityLabel,
} from "../components/Common/DiscoverPriority";
import useFilterPage from "../hooks/useFilterPage";
const svg1 = (
  <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <rect x="0" y="0" width="24" height="24"></rect>
      <circle fill="#000000" cx="5" cy="12" r="2"></circle>
      <circle fill="#000000" cx="12" cy="12" r="2"></circle>
      <circle fill="#000000" cx="19" cy="12" r="2"></circle>
    </g>
  </svg>
);

const DiscoverPriorityTable = () => {
  useFilterPage("discover-priority");
  const imgBaseURl = process.env.REACT_APP_IMAGE_BASEURL;
  const [loader, setLoader] = useState(false);
  const [actionLoader, setActionLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [listLength, setListLength] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("lastActive");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const limit = 50;

  const getTableData = async (page = currentPage) => {
    setLoader(true);
    try {
      const response = await getDiscoverPriorityList(
        page,
        limit,
        search,
        sortBy,
        priorityFilter,
        locationFilter
      );
      const data = response.data?.data || {};
      setUsers(data.users || []);
      const total = data.count || 0;
      setListLength(total);
      setPageCount(Math.ceil(total / limit) || 0);
    } catch (error) {
      notifyError(error.response?.data?.message || "Failed to load list");
      setUsers([]);
      setListLength(0);
      setPageCount(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getTableData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy, priorityFilter, locationFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 0) {
        getTableData(0);
      } else {
        setCurrentPage(0);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const onSearch = () => {
    if (currentPage === 0) {
      getTableData(0);
    } else {
      setCurrentPage(0);
    }
  };

  const updatePriority = async (userId, discoverPriority) => {
    setActionLoader(true);
    try {
      const response = await updateDiscoverPriority({
        userId,
        discoverPriority,
      });
      notifyTopRight(response.data?.message || "Priority updated");
      await getTableData(currentPage);
    } catch (error) {
      notifyError(
        error.response?.data?.message || "Failed to update discover priority"
      );
    } finally {
      setActionLoader(false);
    }
  };

  function formatTimestamp(timestamp) {
    if (!timestamp) return "";
    const isSeconds = String(timestamp).length === 10;
    const ts = isSeconds ? timestamp * 1000 : timestamp;
    return moment(ts).format("DD-MM-YYYY, h:mm A");
  }

  if (loader && users.length === 0) {
    return <Spinner />;
  }

  return (
    <>
      <div className="row g-3 align-items-end mb-3">
        <div className="col-12 col-md-6 col-lg-3">
          <div className="mc-filter-field">
            <label className="form-label fw-semibold">Search</label>
            <div className="input-group mc-search-group">
              <input
                type="search"
                className="form-control"
                placeholder="Search name / email / phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
              />
              <button
                className="btn btn-primary"
                type="button"
                onClick={onSearch}
                aria-label="Search"
              >
                <i className="fa fa-search" />
              </button>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <div className="mc-filter-field">
            <label className="form-label fw-semibold">Priority</label>
            <select
              className="form-control"
              value={priorityFilter}
              onChange={(e) => {
                setCurrentPage(0);
                setPriorityFilter(e.target.value);
              }}
            >
              <option value="">All</option>
              {DISCOVER_PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <div className="mc-filter-field">
            <label className="form-label fw-semibold">Location</label>
            <select
              className="form-control"
              value={locationFilter}
              onChange={(e) => {
                setCurrentPage(0);
                setLocationFilter(e.target.value);
              }}
            >
              <option value="">All</option>
              <option value="Singapore">Singapore</option>
              <option value="Malaysia">Malaysia</option>
            </select>
          </div>
        </div>
        <div className="col-12 col-md-3 col-lg-3">
          <div className="mc-filter-field">
            <label className="form-label fw-semibold">Sort by</label>
            <select
              className="form-control"
              value={sortBy}
              onChange={(e) => {
                setCurrentPage(0);
                setSortBy(e.target.value);
              }}
            >
              <option value="lastActive">Last Active</option>
              <option value="completedAt">Completed At</option>
            </select>
          </div>
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <div className="mc-filter-meta">Total: {listLength}</div>
        </div>
      </div>

      <Table responsive>
        <thead style={{ color: "black" }}>
          <tr>
            <th>
              <strong>Image</strong>
            </th>
            <th>
              <strong>NAME</strong>
            </th>
            <th>
              <strong>EMAIL</strong>
            </th>
            <th>
              <strong>PHONE</strong>
            </th>
            <th>
              <strong>PRIORITY</strong>
            </th>
            <th>
              <strong>Completed At</strong>
            </th>
            <th>
              <strong>Last Active</strong>
            </th>
            <th>
              <strong>ACTION</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          {users?.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-4">
                No tagged female users found.
              </td>
            </tr>
          ) : (
            users.map((item) => (
              <tr
                key={item._id}
                className="pointer"
                onClick={() =>
                  window.open(`/user-details?data=${item?._id}`, "_blank")
                }
                style={{
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#111827",
                }}
              >
                <td>
                  <img
                    src={
                      item?.images?.[0]?.url
                        ? imgBaseURl + item.images[0].url
                        : dummmyImg
                    }
                    width={80}
                    height={70}
                    alt=""
                    style={{ objectFit: "cover", borderRadius: 8 }}
                  />
                </td>
                <td>{item?.name}</td>
                <td>{item?.email}</td>
                <td>
                  {item?.countryCode ? `+${item.countryCode} ` : ""}
                  {item?.phoneNumber || ""}
                </td>
                <td>
                  <Badge
                    bg=""
                    className={`${getDiscoverPriorityBadgeClass(
                      item?.discoverPriority
                    )} fs-12`}
                  >
                    {getDiscoverPriorityLabel(item?.discoverPriority)}
                  </Badge>
                </td>
                <td>
                  {item?.completedAt
                    ? moment(item.completedAt).format("MMM Do YYYY, h:mm A")
                    : ""}
                </td>
                <td>{formatTimestamp(item?.lastApiCall)}</td>
                <td
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="info light"
                      className="light sharp btn btn-info i-false"
                      disabled={actionLoader}
                    >
                      {svg1}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {DISCOVER_PRIORITY_OPTIONS.map((opt) => (
                        <Dropdown.Item
                          key={opt.value}
                          disabled={
                            Number(item?.discoverPriority) === opt.value
                          }
                          onClick={() =>
                            updatePriority(item._id, opt.value)
                          }
                        >
                          Set {opt.label}
                        </Dropdown.Item>
                      ))}
                      <Dropdown.Item
                        onClick={() => updatePriority(item._id, null)}
                      >
                        Remove Tag
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {pageCount > 0 && (
        <Pagination
          pageCount={pageCount}
          pageValue={currentPage}
          setPage={setCurrentPage}
        />
      )}
    </>
  );
};

export default DiscoverPriorityTable;
